/**
 * LegalTemplates PDF Editor — status, hints, tool and suite-mode chrome
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import { redrawAllPages } from './annotations.js';
import {
    alertEl,
    colorInput,
    compressButton,
    compressSourceNameEl,
    convertButton,
    convertFilenameEl,
    deletePagesActionButton,
    docTitleEl,
    documentRequiredTopActions,
    editToolButtons,
    editToolLabels,
    hintEl,
    imageInput,
    mergeButton,
    pagesEl,
    protectActionButton,
    replaceToolEnabled,
    session,
    signatureSubbarEl,
    sizeInput,
    splitExtractActionButton,
    state,
    statusEl,
    subbarEl,
    suiteModeLabels,
    suiteModeMessages,
    suiteToolButtons,
    toolHints,
    watermarkActionButton,
} from './context.js';
import { getBaseFileName, hasLoadedDocumentForActions, toUserFriendlyEditorErrorMessage } from './core.js';
import { commitInlineFormFieldEditor, getFormFillFieldStats, updateFormFillSummary } from './form-fill.js';
import { setSignatureWorkspaceMode } from './io.js';
import {
    closeCompressModal,
    closeConvertModal,
    closeDeletePagesModal,
    closeMergeModal,
    closeProtectModal,
    closeSendModal,
    closeSplitExtractModal,
    closeWatermarkModal,
} from './modals.js';
import { resetPropsPanelDismissal, syncPropsPanel } from './props.js';
import { syncShellMode, syncToolGroups } from './shell.js';
import {
    commitAllInlineEditors,
    commitInlineCommentEditor,
    commitInlineReplaceEditor,
    commitInlineTextEditor,
    updateTextControlVisibility,
} from './text.js';
import { applyToolDefaultColor, syncDragToolTouchAction, syncZoomUi } from './viewport.js';

export function showAlert(message, isError = false) {
    const displayMessage = isError
        ? toUserFriendlyEditorErrorMessage(message)
        : String(message || '');

    alertEl.textContent = displayMessage;
    alertEl.classList.remove('hidden', 'error');
    if (isError) {
        alertEl.classList.add('error');
    }
    window.clearTimeout(showAlert._timer);
    showAlert._timer = window.setTimeout(() => {
        alertEl.classList.add('hidden');
    }, 4200);
}

export function setStatus(message) {
    statusEl.textContent = message;
}

export function syncDocumentNameUi() {
    if (docTitleEl) {
        docTitleEl.textContent = session.docName || 'PDF';
    }

    if (compressSourceNameEl) {
        compressSourceNameEl.textContent = `${getBaseFileName()}.pdf`;
    }

    if (convertFilenameEl) {
        convertFilenameEl.value = getBaseFileName();
    }

    document.title = `${session.docName || 'PDF'} | PDF Editor | LegalTemplates.com`;
}

export function setHint(tool) {
    const toolName = editToolLabels[tool] || (tool.charAt(0).toUpperCase() + tool.slice(1));
    hintEl.textContent = `Tool: ${toolName}. ${toolHints[tool] || ''} Use Save Edits to persist changes.`;
    // Whiteout only paints over content; the hint says so in a warning
    // treatment. (Redact removes the text for real — see redaction.js.)
    hintEl.classList.toggle('pdf-hint--warning', tool === 'erase');
}

export function setModeHint(mode) {
    const label = suiteModeLabels[mode] || 'Tool';
    const message = suiteModeMessages[mode] || 'Select Edit to use annotation tools.';
    hintEl.textContent = `${label} mode selected. ${message}`;
}

export function syncDocumentRequiredActionsDisabledState() {
    const hasDocument = hasLoadedDocumentForActions();
    documentRequiredTopActions.forEach((button) => {
        const isBusy = button.dataset.busy === '1';
        const shouldDisable = isBusy || !hasDocument;
        button.disabled = shouldDisable;
        button.classList.toggle('disabled', shouldDisable);
    });
    syncZoomUi();
}

export function refreshPageCursor() {
    const cursorMode = state.activeSuiteTool === 'edit' ? state.activeTool : '';
    const pageShells = pagesEl.querySelectorAll('.pdf-page-shell');
    pageShells.forEach((pageShell) => {
        if (cursorMode) {
            pageShell.setAttribute('data-edit-cursor', cursorMode);
        } else {
            pageShell.removeAttribute('data-edit-cursor');
        }
    });
}

export function activateTool(tool) {
    if (state.activeSuiteTool !== 'edit') {
        return;
    }

    if (tool === 'replace' && !replaceToolEnabled) {
        showAlert('Replace text is temporarily disabled while we refine this tool.');
        tool = 'text';
    }

    const previousTool = state.activeTool;

    if (state.activeTool === 'text' && tool !== 'text') {
        commitInlineTextEditor(true);
    }

    if (state.activeTool === 'comment' && tool !== 'comment') {
        commitInlineCommentEditor(true);
    }

    if (state.activeTool === 'replace' && tool !== 'replace') {
        commitInlineReplaceEditor(true);
    }

    if (state.activeTool === 'form-fill' && tool !== 'form-fill') {
        commitInlineFormFieldEditor(true);
    }

    state.activeTool = tool;
    syncDragToolTouchAction(tool);
    applyToolDefaultColor(tool);
    editToolButtons.forEach((button) => {
        button.classList.toggle('active', button.dataset.editTool === tool);
    });
    if (tool === 'image' && !state.pendingImage) {
        imageInput.click();
    }
    if (tool === 'form-fill') {
        const stats = getFormFillFieldStats();
        const editableCount = Math.max(0, stats.editable || 0);
        if (editableCount === 0) {
            setStatus('No fillable fields detected. Click Detect Fields to scan underlines, drawn lines, and checkbox symbols.');
        } else if (stats.heuristic > 0 && stats.native === 0) {
            setStatus(`Ready: form-fill (auto-detected ${stats.heuristic} fields)`);
        }
    }
    updateTextControlVisibility();
    refreshPageCursor();
    setHint(tool);
    setStatus(`Ready: ${editToolLabels[tool] || tool}`);
    if (previousTool === 'form-fill' || tool === 'form-fill') {
        redrawAllPages();
    }
    updateFormFillSummary();
    syncToolGroups(tool);
    // A deliberate tool change re-opens a panel the user had closed.
    resetPropsPanelDismissal();
    syncPropsPanel();
}

export function setSuiteMode(mode) {
    if (state.activeSuiteTool === 'edit' && mode !== 'edit') {
        commitAllInlineEditors();
    }

    state.activeSuiteTool = mode;
    syncShellMode(mode);
    syncPropsPanel();

    suiteToolButtons.forEach((button) => {
        button.classList.toggle('active', button.dataset.suiteTool === mode);
    });

    const isEditMode = mode === 'edit';
    const isSignatureMode = mode === 'signature';
    const isConvertMode = mode === 'convert';
    const isCompressMode = mode === 'compress';
    const isMergeMode = mode === 'merge';
    const isDeletePagesMode = mode === 'delete-pages';
    const isSplitExtractMode = mode === 'split-extract';
    const isProtectMode = mode === 'protect';
    const isWatermarkMode = mode === 'watermark';
    subbarEl.classList.toggle('hidden', !isEditMode);
    convertButton.classList.toggle('pdf-force-hidden', !isConvertMode);
    compressButton.classList.toggle('pdf-force-hidden', !isCompressMode);
    mergeButton.classList.toggle('pdf-force-hidden', !isMergeMode);
    if (deletePagesActionButton) {
        deletePagesActionButton.classList.toggle('pdf-force-hidden', !isDeletePagesMode);
    }
    if (splitExtractActionButton) {
        splitExtractActionButton.classList.toggle('pdf-force-hidden', !isSplitExtractMode);
    }
    if (protectActionButton) {
        protectActionButton.classList.toggle('pdf-force-hidden', !isProtectMode);
    }
    if (watermarkActionButton) {
        watermarkActionButton.classList.toggle('pdf-force-hidden', !isWatermarkMode);
    }
    if (signatureSubbarEl) {
        signatureSubbarEl.classList.toggle('hidden', !isSignatureMode);
    }
    updateTextControlVisibility();
    refreshPageCursor();

    if (isEditMode) {
        setSignatureWorkspaceMode(false);
        closeSendModal();
        closeConvertModal();
        closeCompressModal();
        closeMergeModal();
        closeDeletePagesModal();
        closeSplitExtractModal();
        closeProtectModal();
        closeWatermarkModal();
        setHint(state.activeTool);
        setStatus(`Ready: ${editToolLabels[state.activeTool] || state.activeTool}`);
        updateFormFillSummary();
        syncToolGroups(state.activeTool);
        return;
    }

    if (isSignatureMode) {
        setSignatureWorkspaceMode(true);
        closeSendModal();
        closeConvertModal();
        closeCompressModal();
        closeMergeModal();
        closeDeletePagesModal();
        closeSplitExtractModal();
        closeProtectModal();
        closeWatermarkModal();
        setModeHint(mode);
        setStatus('Signature workflow ready');
        state.interaction = null;
        state.draft = null;
        redrawAllPages();
        updateFormFillSummary();
        return;
    }

    setSignatureWorkspaceMode(false);
    closeSendModal();

    if (!isConvertMode) {
        closeConvertModal();
    }

    if (!isCompressMode) {
        closeCompressModal();
    }

    if (!isMergeMode) {
        closeMergeModal();
    }

    if (!isDeletePagesMode) {
        closeDeletePagesModal();
    }

    if (!isSplitExtractMode) {
        closeSplitExtractModal();
    }

    if (!isProtectMode) {
        closeProtectModal();
    }

    if (!isWatermarkMode) {
        closeWatermarkModal();
    }

    setModeHint(mode);
    setStatus(`${suiteModeLabels[mode] || 'Tool'} mode selected`);
    state.interaction = null;
    state.draft = null;
    redrawAllPages();
    updateFormFillSummary();
}

export function getToolSize() {
    return Math.max(1, Number(sizeInput.value || 1));
}

export function getToolColor() {
    return colorInput.value || '#1d4ed8';
}
